import { type FC, useState } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Flag } from 'lucide-react'

interface FlagAccountDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    accountName: string
    accountCount?: number
    onConfirm: (reason: string) => void
    isLoading?: boolean
}

export const FlagAccountDialog: FC<FlagAccountDialogProps> = ({
    open,
    onOpenChange,
    accountName,
    accountCount = 1,
    onConfirm,
    isLoading = false,
}) => {
    const [reason, setReason] = useState('')

    const handleConfirm = () => {
        if (reason.trim()) {
            onConfirm(reason.trim())
            setReason('')
        }
    }

    const handleCancel = () => {
        setReason('')
        onOpenChange(false)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <div className="rounded-full bg-red-100 p-2">
                            <Flag className="h-4 w-4 text-red-600" />
                        </div>
                        <DialogTitle>
                            Flag Account{accountCount > 1 ? 's' : ''}
                        </DialogTitle>
                    </div>
                    <DialogDescription>
                        {accountCount > 1 ? (
                            <>
                                You are about to flag{' '}
                                <span className="font-semibold">
                                    {accountCount} accounts
                                </span>
                                . Please provide a reason for flagging these
                                accounts.
                            </>
                        ) : (
                            <>
                                You are about to flag{' '}
                                <span className="font-semibold">
                                    {accountName}
                                </span>
                                's account. Please provide a reason for flagging
                                this account.
                            </>
                        )}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="reason">Reason for flagging *</Label>
                        <Textarea
                            id="reason"
                            placeholder="Enter the reason for flagging this account..."
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            rows={4}
                            className="resize-none"
                        />
                        <p className="text-xs text-muted-foreground">
                            This reason will be recorded and associated with the
                            flag action.
                        </p>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={handleCancel}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleConfirm}
                        disabled={!reason.trim() || isLoading}
                    >
                        {isLoading
                            ? 'Flagging...'
                            : `Flag Account${accountCount > 1 ? 's' : ''}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
